import { Chip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { registrationPolicyToLabel } from './registration-policy-to-label.map';

export interface RegistrationPolicyBadgeProps {
  policy?: string;
  /** For the details header, which is white on a dark background. */
  onDark?: boolean;
}

/** What a group with no registration policy set at all reads as. */
const NO_POLICY_LABEL = 'None';

// The header's own secondary text and hairline, rather than a second white.
const ON_DARK_TEXT = '#ffffffc4';
const ON_DARK_BORDER = '#ffffff5c';

const useStyles = makeStyles({
  chip: {
    // Under MUI's 24px small chip, which still reads as a control in a dense row.
    height: '20px',
    fontSize: '11px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    verticalAlign: 'middle',
    // MUI's outlined chip is built for a light background and all but vanishes on a dark one.
    color: ({ onDark }: { onDark: boolean }) => (onDark ? ON_DARK_TEXT : undefined),
    borderColor: ({ onDark }: { onDark: boolean }) => (onDark ? ON_DARK_BORDER : undefined)
  },
  label: {
    paddingLeft: '8px',
    paddingRight: '8px'
  }
});

/**
 * Whether people may sign themselves up. Takes the policy, not the group, since a group carries
 * one for kiosks and another for QR.
 *
 * Uncoloured on purpose: these are a setting, not a state, and colour belongs to the status badge
 * beside it. Unset reads "None" — a blank cell would be ambiguous with a failed load, and "None"
 * is not the same claim as "Hidden".
 */
export const RegistrationPolicyBadge = ({ policy, onDark = false }: RegistrationPolicyBadgeProps) => {
  const classes = useStyles({ onDark });

  return (
    <Chip
      // An unrecognised policy shows its raw value, so a new one server-side is visible.
      label={(policy && (registrationPolicyToLabel[policy] ?? policy)) || NO_POLICY_LABEL}
      variant="outlined"
      size="small"
      classes={{ root: classes.chip, label: classes.label }}
    />
  );
};
